import { opensInBuilding, opensObject } from '../../Helpers/keyRegisterUtils';
import { KeyType } from '../../types/keyRegister';
import { KeyObject } from './keyObjects';

/**
 * The places the nyckelregister is narrowed to (the filter modal's tree): per building either the
 * whole building, or its building-wide keys ("Gemensamma nycklar") and/or some of its objects.
 * Picks combine as a union; an empty selection is no filter.
 */

/** 'all': Gemensamma plus every object in the building, including objects added later. */
export type BuildingPick = 'all' | { common: boolean; objectKeys: string[] };
export type KeySelection = { [buildingKey: string]: BuildingPick };

export const EMPTY_SELECTION: KeySelection = {};

export const isSelectionEmpty = (selection: KeySelection): boolean => Object.keys(selection).length === 0;

type PlaceFields = Pick<KeyType, 'opensBuildings' | 'opensUnits' | 'opensStorageUnits'>;

/**
 * Whether the type matches any pick. A whole building: the type opens the building itself or one of
 * its objects. Gemensamma: the type opens the building itself. An object: the type is linked to it.
 */
export const matchesSelection = (type: PlaceFields, selection: KeySelection): boolean => {
    const entries = Object.entries(selection);
    if (entries.length === 0) return true;
    return entries.some(([buildingKey, pick]) => {
        if (pick === 'all') return opensInBuilding(type, buildingKey);
        return (pick.common && !!type.opensBuildings?.[buildingKey])
            || pick.objectKeys.some(objectKey => opensObject(type, objectKey));
    });
};

// --- URL (`?in=`, repeated) ---

/**
 * `?in=` values: `bA` (whole building), `bA.common` (Gemensamma), `bA.u1` (an object). Firebase keys
 * never contain a dot, and URLSearchParams leaves it unencoded.
 */
export const COMMON_TOKEN = 'common';
const SEPARATOR = '.';

/** Reads `?in=`; without it, the older `?building=` (whole building) and `&object=` (that object) links. */
export const parseSelection = (params: URLSearchParams): KeySelection => {
    const values = params.getAll('in');
    if (values.length === 0) {
        const buildingKey = params.get('building');
        if (!buildingKey) return EMPTY_SELECTION;
        const objectKey = params.get('object');
        return { [buildingKey]: objectKey ? { common: false, objectKeys: [objectKey] } : 'all' };
    }
    const selection: KeySelection = {};
    for (const value of values) {
        const [buildingKey, rest] = value.split(SEPARATOR, 2);
        if (!buildingKey) continue;
        if (rest === undefined) {
            selection[buildingKey] = 'all';
            continue;
        }
        const current = selection[buildingKey];
        if (current === 'all' || !rest) continue;
        const pick = current ?? { common: false, objectKeys: [] };
        selection[buildingKey] = rest === COMMON_TOKEN
            ? { ...pick, common: true }
            : { ...pick, objectKeys: pick.objectKeys.includes(rest) ? pick.objectKeys : [...pick.objectKeys, rest] };
    }
    return selection;
};

export const selectionParams = (selection: KeySelection): string[] =>
    Object.entries(selection).flatMap(([buildingKey, pick]) => (pick === 'all'
        ? [buildingKey]
        : [...(pick.common ? [`${buildingKey}${SEPARATOR}${COMMON_TOKEN}`] : []), ...pick.objectKeys.map(k => `${buildingKey}${SEPARATOR}${k}`)]));

// --- Tree state and edits ---

export interface NodeState {
    checked: boolean;
    indeterminate: boolean;
}

const nodeState = (picked: number, total: number): NodeState => ({
    checked: total > 0 && picked === total,
    indeterminate: picked > 0 && picked < total,
});

const explicitPick = (pick: BuildingPick | undefined, objects: KeyObject[]): { common: boolean; objectKeys: string[] } => {
    if (pick === 'all') return { common: true, objectKeys: objects.map(o => o.key) };
    return pick ?? { common: false, objectKeys: [] };
};

/** The building's row: ticked when the whole building is picked, part-ticked when something in it is. */
export const buildingState = (pick: BuildingPick | undefined): NodeState => {
    if (pick === 'all') return { checked: true, indeterminate: false };
    const some = !!pick && (pick.common || pick.objectKeys.length > 0);
    return { checked: false, indeterminate: some };
};

export const commonState = (pick: BuildingPick | undefined): boolean =>
    pick === 'all' || !!pick?.common;

/** A group of objects (or a single object as a group of one). */
export const objectsState = (pick: BuildingPick | undefined, objectKeys: string[]): NodeState => {
    if (pick === 'all') return nodeState(objectKeys.length, objectKeys.length);
    const picked = new Set(pick?.objectKeys ?? []);
    return nodeState(objectKeys.filter(k => picked.has(k)).length, objectKeys.length);
};

/** Everything picked becomes 'all'; nothing picked drops the building. */
const settle = (selection: KeySelection, buildingKey: string, pick: { common: boolean; objectKeys: string[] }, objects: KeyObject[]): KeySelection => {
    const { [buildingKey]: _previous, ...rest } = selection;
    if (!pick.common && pick.objectKeys.length === 0) return rest;
    const picked = new Set(pick.objectKeys);
    if (pick.common && objects.every(o => picked.has(o.key))) return { ...rest, [buildingKey]: 'all' };
    return { ...rest, [buildingKey]: pick };
};

/** Ticks the whole building, or clears it when it is already whole. */
export const toggleBuilding = (selection: KeySelection, buildingKey: string): KeySelection => {
    const { [buildingKey]: previous, ...rest } = selection;
    return previous === 'all' ? rest : { ...rest, [buildingKey]: 'all' };
};

export const setCommon = (selection: KeySelection, buildingKey: string, on: boolean, objects: KeyObject[]): KeySelection =>
    settle(selection, buildingKey, { ...explicitPick(selection[buildingKey], objects), common: on }, objects);

/** Ticks or clears these objects (one object, or a group). */
export const setObjects = (selection: KeySelection, buildingKey: string, objectKeys: string[], on: boolean, objects: KeyObject[]): KeySelection => {
    const pick = explicitPick(selection[buildingKey], objects);
    const keys = new Set(pick.objectKeys);
    for (const key of objectKeys) {
        if (on) keys.add(key); else keys.delete(key);
    }
    // Keep the building's object order.
    const order = objects.map(o => o.key);
    const sorted = Array.from(keys).sort((a, b) => {
        const ia = order.indexOf(a);
        const ib = order.indexOf(b);
        return (ia < 0 ? Infinity : ia) - (ib < 0 ? Infinity : ib);
    });
    return settle(selection, buildingKey, { ...pick, objectKeys: sorted }, objects);
};

// --- Chips ---

/** How many names a building's chip lists before "+N". */
export const CHIP_NAME_LIMIT = 2;

/** "allt", or "gemensamma, Lägenhet 1001 +2". Objects not (yet) loaded count in "+N". */
export const pickLabel = (pick: BuildingPick, objects: KeyObject[]): string => {
    if (pick === 'all') return 'allt';
    const names = [
        ...(pick.common ? ['gemensamma'] : []),
        ...pick.objectKeys.map(k => objects.find(o => o.key === k)?.name).filter((n): n is string => !!n),
    ];
    const total = (pick.common ? 1 : 0) + pick.objectKeys.length;
    const shown = names.slice(0, CHIP_NAME_LIMIT);
    const rest = total - shown.length;
    return shown.join(', ') + (rest > 0 ? (shown.length > 0 ? ` +${rest}` : `${rest} objekt`) : '');
};
